import { Link, router } from '@inertiajs/react'
import { useState } from 'react'
import PlatformLayout from '@/components/PlatformLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { ArrowLeft, Copy, Check, KeyRound, Pencil, Trash2, Power, PowerOff, Eraser } from 'lucide-react'

interface School {
  id: number
  name: string
  description: string | null
  phone: string | null
  address: string | null
  city: string | null
  state: string | null
  postal_code: string | null
  country: string | null
  legal_name: string | null
  cnpj: string | null
  privacy_contact_email: string | null
  timezone: string | null
  slug: string | null
  active: boolean
  deactivated_at: string | null
  created_at: string
}

interface Owner {
  id: number
  name: string
  email: string
  phone: string
  verified: boolean
}

interface Stats {
  students_count: number
  teachers_count: number
  managers_count: number
  lessons_total: number
  revenue_total: number
}

interface Props {
  school: School
  owner: Owner | null
  stats: Stats
  generated_password?: string | null
}

const fmtBRL = (n: number) =>
  n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

export default function Show({ school, owner, stats, generated_password }: Props) {
  const [copied, setCopied] = useState(false)
  const ref = school.slug || school.id

  const toggleActive = () => {
    router.patch(`/platform/schools/${ref}/toggle_active`, {}, { preserveScroll: true })
  }

  const resetData = () => {
    router.post(`/platform/schools/${ref}/reset_data`, {}, { preserveScroll: true })
  }

  const remove = () => {
    router.delete(`/platform/schools/${ref}`)
  }

  const resetPassword = () => {
    router.post(`/platform/schools/${ref}/reset_owner_password`, {}, { preserveScroll: true })
  }

  const copyPassword = async () => {
    if (!generated_password) return
    await navigator.clipboard.writeText(generated_password)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const fields: Array<[string, string | null]> = [
    ['Telefone', school.phone],
    ['CNPJ', school.cnpj],
    ['Razão social', school.legal_name],
    ['Endereço', school.address],
    ['Cidade', [school.city, school.state].filter(Boolean).join(' / ')],
    ['CEP', school.postal_code],
    ['País', school.country],
    ['Email de privacidade', school.privacy_contact_email],
    ['Slug', school.slug],
  ]

  return (
    <PlatformLayout>
      <div className="p-6 max-w-4xl space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Link href="/platform/schools" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              <ArrowLeft className="h-3 w-3" /> Escolas
            </Link>
            <div className="flex items-center gap-3 mt-1">
              <h1 className="text-2xl font-bold tracking-tight">{school.name}</h1>
              {school.active ? (
                <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-950 dark:text-emerald-300">Ativa</Badge>
              ) : (
                <Badge variant="destructive">Desativada</Badge>
              )}
            </div>
            {school.description && <p className="text-sm text-muted-foreground mt-1">{school.description}</p>}
          </div>
          <Link href={`/platform/schools/${school.id}/edit`}>
            <Button variant="outline" size="sm">
              <Pencil className="h-4 w-4 mr-2" />
              Editar
            </Button>
          </Link>
        </div>

        {!school.active && (
          <Card className="border-destructive/40 bg-destructive/5">
            <CardContent className="py-4 flex items-center gap-3">
              <PowerOff className="h-5 w-5 text-destructive shrink-0" />
              <p className="text-sm">
                Esta escola está <span className="font-semibold">desativada</span>. Alunos não conseguem logar nem usar o app,
                e o owner não acessa o painel. Reative na Zona de perigo abaixo.
              </p>
            </CardContent>
          </Card>
        )}

        {generated_password && (
          <Card className="border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40">
            <CardHeader className="pb-2">
              <CardTitle className="text-amber-900 dark:text-amber-200 text-base flex items-center gap-2">
                <KeyRound className="h-4 w-4" />
                Senha do owner — copie agora
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-amber-900/80 dark:text-amber-200/80">
                Esta senha aparece apenas uma vez. Anote ou envie para {owner?.email ?? 'o owner'} antes de sair desta página.
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded-md border border-amber-300 dark:border-amber-700 bg-white dark:bg-amber-950 px-3 py-2 font-mono text-base tracking-wider">
                  {generated_password}
                </code>
                <Button variant="outline" size="sm" onClick={copyPassword}>
                  {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                  {copied ? 'Copiado' : 'Copiar'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat label="Alunos" value={stats.students_count} />
          <Stat label="Professores" value={stats.teachers_count} />
          <Stat label="Gerentes" value={stats.managers_count} />
          <Stat label="Aulas" value={stats.lessons_total} />
          <Stat label="Receita" value={fmtBRL(stats.revenue_total)} />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Dados da escola</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {fields.map(([label, value]) => (
                <div key={label}>
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="font-medium">{value || <span className="text-muted-foreground italic">—</span>}</dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle>Owner</CardTitle>
            {owner && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <KeyRound className="h-4 w-4 mr-2" />
                    Gerar nova senha
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Gerar nova senha?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Uma nova senha será gerada para <span className="font-semibold">{owner.email}</span>. A senha atual deixará de funcionar imediatamente. A nova senha aparecerá nesta tela uma única vez — anote ou envie ao owner antes de sair.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={resetPassword}>
                      Gerar nova senha
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </CardHeader>
          <CardContent>
            {owner ? (
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-muted-foreground">Nome</dt>
                  <dd className="font-medium">{owner.name}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Email</dt>
                  <dd className="font-medium">{owner.email}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Telefone</dt>
                  <dd className="font-medium">{owner.phone}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Verificado</dt>
                  <dd className="font-medium">{owner.verified ? 'Sim' : 'Não'}</dd>
                </div>
              </dl>
            ) : (
              <p className="text-sm text-muted-foreground italic">Sem owner cadastrado.</p>
            )}
          </CardContent>
        </Card>

        {/* Zona de perigo */}
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="text-destructive">Zona de perigo</CardTitle>
          </CardHeader>
          <CardContent className="divide-y">
            {/* Ativar / desativar */}
            <DangerRow
              title={school.active ? 'Desativar escola' : 'Reativar escola'}
              description={
                school.active
                  ? 'Bloqueia login e API para alunos e owner. Os dados são preservados e podem ser reativados a qualquer momento.'
                  : 'Reabre o acesso ao app e ao painel. Alunos e owner voltam a logar normalmente.'
              }
              action={
                school.active ? (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="border-amber-400 text-amber-700 hover:bg-amber-50 dark:text-amber-300">
                        <PowerOff className="h-4 w-4 mr-2" />
                        Desativar
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Desativar {school.name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          A escola deixará de aparecer na API e ninguém conseguirá logar (nem os alunos no app, nem o owner no painel).
                          Nenhum dado é apagado — você pode reativar quando quiser.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={toggleActive} className="bg-amber-600 hover:bg-amber-700">
                          Desativar escola
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                ) : (
                  <Button variant="outline" size="sm" onClick={toggleActive} className="border-emerald-400 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300">
                    <Power className="h-4 w-4 mr-2" />
                    Reativar
                  </Button>
                )
              }
            />

            {/* Limpar dados */}
            <DangerRow
              title="Limpar dados"
              description="Apaga alunos, professores, aulas, pagamentos, assinaturas, eventos e notificações — deixa a escola como recém-criada. Owner e configurações são preservados."
              action={
                <ConfirmByNameDialog
                  schoolName={school.name}
                  triggerLabel="Limpar dados"
                  triggerIcon={<Eraser className="h-4 w-4 mr-2" />}
                  title="Limpar dados da escola?"
                  confirmLabel="Limpar dados"
                  onConfirm={resetData}
                >
                  Todo o histórico operacional de <span className="font-semibold">{school.name}</span> será apagado
                  permanentemente (alunos, professores, aulas, pagamentos, assinaturas, eventos, notificações).
                  A escola, o owner e as configurações permanecem. <span className="font-semibold">Esta ação não pode ser desfeita.</span>
                </ConfirmByNameDialog>
              }
            />

            {/* Remover escola */}
            <DangerRow
              title="Remover escola"
              description="Exclui a escola e absolutamente todos os seus dados, incluindo o owner. Irreversível."
              action={
                <ConfirmByNameDialog
                  schoolName={school.name}
                  triggerLabel="Remover"
                  triggerIcon={<Trash2 className="h-4 w-4 mr-2" />}
                  triggerVariant="destructive"
                  title="Remover escola?"
                  confirmLabel="Remover escola"
                  destructive
                  onConfirm={remove}
                >
                  A escola <span className="font-semibold">{school.name}</span> e todos os seus dados associados
                  (alunos, professores, aulas, pagamentos, owner) serão removidos permanentemente.
                  <span className="font-semibold"> Esta ação não pode ser desfeita.</span>
                </ConfirmByNameDialog>
              }
            />
          </CardContent>
        </Card>
      </div>
    </PlatformLayout>
  )
}

function DangerRow({ title, description, action }: { title: string; description: string; action: React.ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-4 first:pt-0 last:pb-0">
      <div>
        <p className="font-medium text-sm">{title}</p>
        <p className="text-sm text-muted-foreground max-w-xl">{description}</p>
      </div>
      <div className="shrink-0">{action}</div>
    </div>
  )
}

function ConfirmByNameDialog({
  schoolName,
  triggerLabel,
  triggerIcon,
  triggerVariant = 'outline',
  title,
  confirmLabel,
  destructive = false,
  onConfirm,
  children,
}: {
  schoolName: string
  triggerLabel: string
  triggerIcon?: React.ReactNode
  triggerVariant?: 'outline' | 'destructive'
  title: string
  confirmLabel: string
  destructive?: boolean
  onConfirm: () => void
  children: React.ReactNode
}) {
  const [value, setValue] = useState('')
  const match = value.trim() === schoolName.trim()

  return (
    <AlertDialog onOpenChange={() => setValue('')}>
      <AlertDialogTrigger asChild>
        <Button variant={triggerVariant} size="sm" className={destructive ? '' : 'border-destructive/40 text-destructive hover:bg-destructive/10'}>
          {triggerIcon}
          {triggerLabel}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{children}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <Label htmlFor="confirm-name" className="text-sm">
            Digite <span className="font-semibold">{schoolName}</span> para confirmar
          </Label>
          <Input
            id="confirm-name"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={schoolName}
            autoComplete="off"
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            disabled={!match}
            onClick={onConfirm}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-xl font-bold mt-1">{value}</p>
      </CardContent>
    </Card>
  )
}
